import {
  AnomalySensitivity,
  AutomatedTestAnomalyConfig,
  AutomatedTestConfigurationApi,
  DataContractConfigApi,
  FetchMetadataValuesApi,
  FetchReportApi,
  Report,
  SensitivityEstimation,
  Test,
  TestPollingApi,
  TestResult,
  TestRun,
  TestsRunsCountByHour,
  Totals,
  UpdateCloudTestResponse,
  UpdateCloudTestsResponse,
  UpdateTestApi,
} from '@elementary/common-ui/types';
import {
  get,
  put,
  post,
  patch,
  del,
  getStreamingJSON,
} from '@elementary/common-ui/utils/http';
import { gql } from 'graphql-request';
import {
  CreateOverrideAlertRuleApi,
  FetchAlertIntegrationOptionsApi,
  FlagsApi,
  FetchPullRequestsApi,
  FetchTestsApi,
  AddTestApi,
  AddSingularTestApi,
  ConvertQueryApi,
  ConvertQueryApiResult,
  FetchDwhApi,
  EditGenericTestApi,
  FetchIntegrationDestinationOptionsApi,
  AddCloudTestApi,
  FetchAssetResourcesApi,
} from '../types';

export const fetchExtendedMetadataValueOptions = (
  envId: string,
): Promise<FetchMetadataValuesApi> =>
  get(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/${envId}/resources/extended-metadata-options`,
  );

export const fetchFlags = (): Promise<FlagsApi> =>
  get(`${import.meta.env.VITE_API_BASE_URL}/flags/`);

export const fetchPullRequests = (
  envId: string,
): Promise<FetchPullRequestsApi> =>
  get(
    `${import.meta.env.VITE_API_BASE_URL}/environments/${envId}/pull-requests`,
  );

export const fetchReport = async (
  envId: string,
  accessToken?: string,
): Promise<FetchReportApi> => {
  const url = `${import.meta.env.VITE_API_BASE_URL}/report/json/${envId}`;

  try {
    // Always use streaming for better memory management with large reports
    const result = await getStreamingJSON<Report>(url, {}, accessToken);

    if (!result) {
      throw new Error('Streaming returned null/undefined result');
    }

    // Extract the actual value from @streamparser/json result
    const reportData = (result as any)?.value || result;

    return reportData;
  } catch (error) {
    return get(url);
  }
};

export const fetchCloudTests = (envId: string): Promise<Array<Test>> =>
  get(`${import.meta.env.VITE_API_BASE_URL}/cloud-tests/${envId}/tests`);

export const fetchCloudTestsResults = (
  envId: string,
): Promise<Array<TestResult>> =>
  get(`${import.meta.env.VITE_API_BASE_URL}/cloud-tests/${envId}/test-results`);

export const fetchCloudTestsRuns = (envId: string): Promise<Array<TestRun>> =>
  get(`${import.meta.env.VITE_API_BASE_URL}/cloud-tests/${envId}/test-runs`);

export const fetchTestsRunsCountByHour = (
  envId: string,
): Promise<TestsRunsCountByHour> =>
  get(`${import.meta.env.VITE_API_BASE_URL}/cloud-tests/${envId}/runs-by-hour`);

export const fetchTestResultsTotals = (
  envId: string,
): Promise<Record<string, Totals>> =>
  get(
    `${import.meta.env.VITE_API_BASE_URL}/cloud-tests/${envId}/results-totals`,
  );

export const fetchTestRunsTotals = (
  envId: string,
): Promise<Record<string, Totals>> =>
  get(`${import.meta.env.VITE_API_BASE_URL}/cloud-tests/${envId}/runs-totals`);

export const updateCloudTestConfig = (
  envId: string,
  testId: string,
  config: AutomatedTestConfigurationApi,
): Promise<void> =>
  put(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/cloud-tests/${envId}/tests/${testId}/config`,
    config,
  );

export const updateDataContractConfig = (
  envId: string,
  testId: string,
  config: DataContractConfigApi,
): Promise<void> =>
  put(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/cloud-tests/${envId}/tests/${testId}/data-contract-config`,
    config,
  );

export const fetchCurrentSchema = (
  envId: string,
  testId: string,
): Promise<{
  columns: NonNullable<DataContractConfigApi['baseline_columns']>;
}> =>
  get(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/cloud-tests/${envId}/tests/${testId}/current-schema`,
  );

export const updateTest = (
  envId: string,
  testId: string,
  data: UpdateTestApi,
): Promise<UpdateCloudTestResponse> =>
  patch(
    `${import.meta.env.VITE_API_BASE_URL}/cloud-tests/${envId}/tests/${testId}`,
    data,
  );

export const updateCloudTests = (
  envId: string,
  tests: { test_id: string; disabled: boolean }[],
): Promise<UpdateCloudTestsResponse> =>
  patch(
    `${import.meta.env.VITE_API_BASE_URL}/cloud-tests/${envId}/tests`,
    tests,
  );

export const simulateCloudTestResult = (
  envId: string,
  resultId: string | undefined,
  anomalyConfig: AutomatedTestAnomalyConfig,
): Promise<TestResult> =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/cloud-tests/${envId}/test-results/${resultId}/simulate`,
    {
      anomaly_config: anomalyConfig,
    },
  );

export const getCloudTestResultEstimations = async (
  envId: string,
  resultId: string | undefined,
  sensitivities: AnomalySensitivity[],
): Promise<SensitivityEstimation[]> => {
  const result = await post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/cloud-tests/${envId}/test-results/${resultId}/estimate`,
    {
      sensitivities,
    },
  );
  return result.estimations;
};

export const pollTestsBulk = (
  envId: string,
  testIds: string[],
): Promise<{ results: TestPollingApi[] }> =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/cloud-tests/${envId}/tests/bulk-last-run-state`,
    { test_ids: testIds },
  );

export const fetchMetadata = (envId: string): Promise<FetchMetadataValuesApi> =>
  get(`${import.meta.env.VITE_API_BASE_URL}/${envId}/resources/metadata`);

export const setIsCriticalAssets = ({
  ids,
  isCritical,
  envId,
}: {
  ids: string[];
  isCritical: boolean;
  envId: string;
}) =>
  patch(`${import.meta.env.VITE_API_BASE_URL}/${envId}/assets/`, {
    asset_ids: ids,
    is_critical: isCritical,
  });

export const createCustomAlertOverride = (
  envId: string,
  destinations: CreateOverrideAlertRuleApi,
  entityIds: string[],
) =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/environments/${envId}/tests/override-alerts`,
    { destinations, entity_ids: entityIds },
  );

export const deleteRule = (ruleId: string, envId: string) =>
  del(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/environments/${envId}/routing-rules/${ruleId}`,
    {},
  );

export const deleteDigestRule = (ruleId: string, envId: string) =>
  del(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/environments/${envId}/digest-rules/${ruleId}`,
    {},
  );

export const deleteAlertDestinations = (
  envId: string,
  destinationIds: string[],
  entityIds: string[],
) =>
  del(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/environments/${envId}/tests/override-alerts`,
    {
      data: {
        destination_ids: destinationIds,
        entity_ids: entityIds,
      },
    },
  );

export const fetchAlertIntegrations =
  (): Promise<FetchAlertIntegrationOptionsApi> =>
    get(`${import.meta.env.VITE_API_BASE_URL}/account/integrations/`);

export const fetchResources = (
  envId: string,
): Promise<FetchAssetResourcesApi> =>
  get(`${import.meta.env.VITE_API_BASE_URL}/test-management/assets/${envId}`);

export const fetchTestManagementTests = (
  envId: string,
): Promise<FetchTestsApi> =>
  get(`${import.meta.env.VITE_API_BASE_URL}/test-management/tests/${envId}`);

export const addTests = (args: AddTestApi, envId: string, ticketId?: string) =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/environments/${envId}/pull-requests/add_generic_tests`,
    {
      data: { new_tests: args },
      ...(ticketId ? { ticket_id: ticketId } : {}),
    },
  );

export const addYamlTests = (
  args: AddTestApi,
  envId: string,
  ticketId?: string,
) =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/environments/${envId}/pull-requests/add_generic_tests`,
    {
      data: { yaml_tests: args },
      ...(ticketId ? { ticket_id: ticketId } : {}),
    },
  );

export const addSingularTest = (
  args: AddSingularTestApi,
  envId: string,
  ticketId?: string,
) =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/environments/${envId}/pull-requests/add_singular_test`,
    {
      data: args,
      ...(ticketId ? { ticket_id: ticketId } : {}),
    },
  );

export const checkDuplicateCloudTests = (
  args: AddCloudTestApi,
  envId: string,
) =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/cloud-tests/${envId}/tests/check-duplicates`,
    args,
  );

export const addCloudTest = (
  args: AddCloudTestApi,
  envId: string,
  override?: boolean,
) =>
  post(
    `${import.meta.env.VITE_API_BASE_URL}/cloud-tests/${envId}/tests${
      override ? '?override_existing=true' : ''
    }`,
    args,
  );

export const convertQuery = (
  args: ConvertQueryApi,
  envId: string,
): Promise<ConvertQueryApiResult> =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/test-management/${envId}/convert-query`,
    args,
  );

export const fetchDwh = (envId: string): Promise<FetchDwhApi> =>
  get(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/test-management/data-warehouse/${envId}`,
  );

export const editGenericTest = (
  args: { data: { edit_tests: EditGenericTestApi[] } },
  envId: string,
  ticketId?: string,
): Promise<void> =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/environments/${envId}/pull-requests/edit_generic_tests`,
    {
      ...args,
      ...(ticketId ? { ticket_id: ticketId } : {}),
    },
  );

export const fetchDestinationOptions =
  (): Promise<FetchIntegrationDestinationOptionsApi> =>
    get(
      `${
        import.meta.env.VITE_API_BASE_URL
      }/account/integrations/destination-options`,
    );

export const muteTest = (envId: string, testId: string) =>
  post(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/environments/${envId}/tests/${testId}/mute`,
  );

export const unMuteTest = (envId: string, testId: string) =>
  del(
    `${
      import.meta.env.VITE_API_BASE_URL
    }/environments/${envId}/tests/${testId}/mute`,
  );

export const editTestsNotification = (
  envId: string,
  ids: string[],
  mute: boolean,
) =>
  patch(
    `${import.meta.env.VITE_API_BASE_URL}/environments/${envId}/alert-configs`,
    {
      entity_ids: ids,
      is_muted: mute,
    },
  );

export const getCloudTestsQuery = gql`
  query GetCloudTests($includeAlertDestinations: Boolean = false) {
    cloudTests: tests(testSource: CLOUD) {
      id
      name
      type
      severity
      owners
      tags
      resource {
        name
        type
      }
      tableAsset {
        id
        dbName
        schemaName
        tableName
        owners
        tags
        lastDataModificationTime
        materialization
      }
      updatedAt
      config {
        muted
      }
      alertDestinations @include(if: $includeAlertDestinations) {
        id
        type
        channel
        configurationType
        ruleId
      }
    }
  }
`;

export const getAssetQuery = gql`
  query GetAsset($id: String!, $includeTestExecutions: Boolean = false) {
    asset(id: $id) {
      id
      name
      type
      displayType
      artifactType
      description
      owners
      subscribers
      tags
      fqn
      parentId
      path
      additionalProperties
      columns {
        id
        name
        displayName
        dataType
        description
        tags
      }
      testExecutionsByQualityDimension @include(if: $includeTestExecutions) {
        dimension
        totals {
          passed
          warnings
          failures
          errors
          skipped
        }
      }
      ... on TableAsset {
        configPath
        dbName
        schemaName
        tableName
        materialization
        lastDataModificationTime
        latestExecution {
          id
          status
          executionTimestamp
          query
        }
        latestTestExecutions {
          test {
            name
          }
          execution {
            status
            subType
            executionTimestamp
            columnName
          }
        }
        tableUsage {
          periodStart
          periodEnd
          queriesCount
          uniqueUserCount
          usagePercentile
        }
      }
      ... on BiAsset {
        platform
        biType
        url
      }
      ... on SemanticAsset {
        platform
        semanticType
        dbName
        schemaName
      }
      ... on EtlAsset {
        latestExecution {
          id
          status
          executionTimestamp
          query
        }
      }
    }
  }
`;

export const getSemanticElementsQuery = gql`
  query GetSemanticElements($parentIds: [String!]!) {
    assets(parentIds: $parentIds) {
      items {
        id
        name
        description
        artifactType
        columns {
          id
          name
          displayName
          dataType
          description
        }
        ... on SemanticAsset {
          semanticType
        }
      }
    }
  }
`;

export const getLatestExecutionQuery = gql`
  query GetLatestExecution($id: String!) {
    asset(id: $id) {
      ... on TableAsset {
        latestExecution(ignoreSkippedRuns: true) {
          query
        }
      }
    }
  }
`;

export const getTestDataQuery = gql`
  query GetTestData($id: String!) {
    latestTestExecution(id: $id) {
      test {
        id
        name
        description
        path
      }
      execution {
        id
        status
        executionTimestamp
        query
      }
    }
  }
`;
